import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import gsap from 'gsap'
import styled from 'styled-components'
import Img from 'gatsby-image'

import { HeadlineM } from 'components/Text'

import { ReactComponent as PickAndPlaceSVG } from 'svg/pickAndPlace.svg'
import { ReactComponent as PackagingSVG } from 'svg/packaging.svg'
import { ReactComponent as PalletizingSVG } from 'svg/palletizing.svg'
import { ReactComponent as AssemblySVG } from 'svg/assembly.svg'
import { ReactComponent as InspectionSVG } from 'svg/inspection.svg'
import { ReactComponent as BinPickingSVG } from 'svg/binPicking.svg'
import { ReactComponent as FasteningSVG } from 'svg/fastening.svg'
import { ReactComponent as WeldingSVG } from 'svg/welding.svg'
import { ReactComponent as MachineTendingSVG } from 'svg/machineTending.svg'
import { FluidImage } from 'Types'
import { CircleButton } from 'components/Buttons'
import media from 'styles/media'

type Props = {
  data: {
    pickAndPlace: FluidImage
    packaging: FluidImage
    palletizing: FluidImage
    assembly: FluidImage
    inspection: FluidImage
    binPicking: FluidImage
    fastening: FluidImage
    welding: FluidImage
    machineTending: FluidImage
  }
}

const Applications: React.FC<Props> = ({ data }) => {
  const [step, setStep] = useState(1)
  const [stepLimit, setStepLimit] = useState(7)
  const list: any = useRef()
  const isAnimating = useRef(false)

  useEffect(() => {
    setStepLimit(window.innerWidth > 1200 ? 7 : 9)
    window.addEventListener('resize', () => {
      setStepLimit(window.innerWidth > 1200 ? 7 : 9)
    })
    return () => {
      window.removeEventListener('resize', () => {
        setStepLimit(window.innerWidth > 1200 ? 7 : 9)
      })
    }
  }, [])
  const next = useCallback(() => {
    if (isAnimating.current) {
      return
    }
    isAnimating.current = true
    setStep(step + 1)
    gsap.to('.applications__list', {
      x: stepLimit === 7 ? '-=472' : '-=337',
      duration: 0.8,
      ease: 'camCustom',
      onComplete: () => {
        isAnimating.current = false
      },
    })
  }, [step, stepLimit])

  const prev = useCallback(() => {
    if (isAnimating.current) {
      return
    }
    isAnimating.current = true
    setStep(step - 1)
    gsap.to('.applications__list', {
      x: stepLimit === 7 ? '+=472' : '+=337',
      duration: 0.8,
      ease: 'camCustom',
      onComplete: () => {
        isAnimating.current = false
      },
    })
  }, [step, stepLimit])

  const handlers = useSwipeable({
    onSwipedRight: () => {
      if (step !== 1) {
        prev()
      }
    },
    onSwipedLeft: () => {
      if (step !== stepLimit) {
        next()
      }
    },
  })

  return (
    <Wrapper>
      <Title>Applications</Title>
      <Stepper>
        <Button disabled={step === 1} onClick={prev} aria-label="Previous" />
        <Button
          onClick={next}
          disabled={step === stepLimit}
          aria-label="Next"
        />
      </Stepper>
      <ApplicationsList ref={list} {...handlers} className="applications__list">
        <SvgWrapper inView={step === 1 || stepLimit === 9}>
          <PickAndPlaceSVG />
          <Image>
            <Img fluid={data.pickAndPlace.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={step === 1 || step === 2 || stepLimit === 9}>
          <PackagingSVG />
          <Image>
            <Img fluid={data.packaging.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={step <= 3 || stepLimit === 9}>
          <PalletizingSVG />
          <Image>
            <Img fluid={data.palletizing.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={(step <= 4 && step >= 2) || stepLimit === 9}>
          <AssemblySVG />
          <Image>
            <Img fluid={data.assembly.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={(step <= 5 && step >= 3) || stepLimit === 9}>
          <InspectionSVG />
          <Image>
            <Img fluid={data.inspection.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={(step <= 6 && step >= 4) || stepLimit === 9}>
          <BinPickingSVG />
          <Image>
            <Img fluid={data.binPicking.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={(step <= 7 && step >= 5) || stepLimit === 9}>
          <FasteningSVG />
          <Image>
            <Img fluid={data.fastening.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={step === 6 || step === 7 || stepLimit === 9}>
          <WeldingSVG />
          <Image>
            <Img fluid={data.welding.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
        <SvgWrapper inView={step >= 7 || stepLimit === 9}>
          <MachineTendingSVG />
          <Image>
            <Img fluid={data.machineTending.childImageSharp.fluid} />
          </Image>
        </SvgWrapper>
      </ApplicationsList>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  position: relative;
  height: 916px;
  width: 100%;
  overflow: hidden;
  padding-left: 96px;
  padding-top: 200px;
  ${media.tabletLandscape} {
    padding: 48px 72px;
    height: 630px;
  }
  ${media.tablet} {
    padding-left: 48px;
  }
  ${media.mobile} {
    padding-left: 12px;
  }
`
const Title = styled(HeadlineM)`
  margin-bottom: 47px;
  ${media.mobile} {
    font-size: 36px;
  }
`
const Stepper = styled.div`
  width: 168px;
  position: absolute;
  right: 222px;
  display: flex;
  justify-content: space-between;
  top: 191px;
  ${media.tabletLandscape} {
    left: 72px;
    top: 547px;
  }
  ${media.tablet} {
    left: 48px;
  }
  ${media.mobile} {
    left: 12px;
  }
`
const Button = styled(CircleButton)`
  &:nth-child(1) {
    transform: rotate(180deg);
    :hover {
      transform: rotate(180deg) scale(1.05);
    }
    :active {
      transform: rotate(180deg) scale(1) !important ;
    }
    :disabled {
      :hover {
        transform: rotate(180deg) scale(1);
      }
    }
  }
  :disabled {
    opacity: 0.24;
    :hover {
      transform: none;
    }
  }
`
const ApplicationsList = styled.div`
  display: flex;
  width: fit-content;
`
const SvgWrapper = styled.div<{ inView: boolean }>`
  width: 400px;
  position: relative;
  margin-right: 72px;
  opacity: ${(props) => (props.inView ? 1 : 0.24)};
  transition: opacity 800ms cubic-bezier(0.72, 0, 0.12, 1);
  ${media.tabletLandscape} {
    width: 280px;
    margin-right: 57px;
  }
`
const Image = styled.div`
  width: 360px;
  position: absolute;
  top: 24px;
  left: 64px;
  ${media.tabletLandscape} {
    width: 252px;
    top: 16px;
    left: 44px;
  }
`

export default Applications
